/**
 * Generated by orval 🍺
 * Do not edit manually.
 * CommunityCliQ API
 *  ## Authentication
 *
 *   Every request must include an API access token generated in the CommunityCliQ \
 *   application:
 *
 *   ```
 *   Authorization: X-API-TOKEN
 *   ```
 *
 * OpenAPI spec version: V2.X
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  CommandErrorResponse,
  GetUsersParams,
  PageUserAdminListDetails,
  UpdateUserLocaleRequest,
  UserEventParticipationDetails,
  UserFieldDetails,
  UserPublicDetails,
  UserStatisticsDetails,
  ViewedRecordingDetails,
} from "../../model";

import { customInstance } from "../../../lib/SpringAxios";
import type { ErrorType } from "../../../lib/SpringAxios";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") {
      continue;
    }
    Object.defineProperty(result, key, {
      configurable: true,
      enumerable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const updateUserLocale = (
  updateUserLocaleRequest: UpdateUserLocaleRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      data: updateUserLocaleRequest,
      headers: { "Content-Type": "application/json" },
      method: "PUT",
      signal,
      url: `/api/v2/users/me/locale`,
    },
    options,
  );
};

export const getUpdateUserLocaleMutationKey = () =>
  ["updateUserLocale"] as const;

export const getUpdateUserLocaleMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateUserLocale>>,
    TError,
    UpdateUserLocaleMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateUserLocale>>,
  TError,
  UpdateUserLocaleMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateUserLocaleMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateUserLocale>>,
    UpdateUserLocaleMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return updateUserLocale(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateUserLocaleMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateUserLocale>>
>;
export type UpdateUserLocaleMutationBody = UpdateUserLocaleRequest;
export type UpdateUserLocaleMutationError = ErrorType<CommandErrorResponse>;
export type UpdateUserLocaleMutationVariables = {
  data: UpdateUserLocaleRequest;
};

export const useUpdateUserLocale = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateUserLocale>>,
      TError,
      UpdateUserLocaleMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateUserLocale>>,
  TError,
  UpdateUserLocaleMutationVariables,
  TContext
> => {
  return useMutation(getUpdateUserLocaleMutationOptions(options), queryClient);
};
/**
 * Get the list of active users in the application.
 * @summary Get the list of active users in the application.
 */
export const getUsers = (
  params?: GetUsersParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<PageUserAdminListDetails>(
    { method: "GET", params, signal, url: `/api/v2/users` },
    options,
  );
};

export const getGetUsersQueryKey = (params?: GetUsersParams) => {
  return [`/api/v2/users`, ...(params ? [params] : [])] as const;
};

export const getGetUsersQueryOptions = <
  TData = Awaited<ReturnType<typeof getUsers>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  params?: GetUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUsers>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUsersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUsers>>> = ({
    signal,
  }) => getUsers(params, requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUsers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUsersQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUsers>>
>;
export type GetUsersQueryError = ErrorType<CommandErrorResponse>;

export function useGetUsers<
  TData = Awaited<ReturnType<typeof getUsers>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  params: undefined | GetUsersParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUsers>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsers>>,
          TError,
          Awaited<ReturnType<typeof getUsers>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsers<
  TData = Awaited<ReturnType<typeof getUsers>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  params?: GetUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUsers>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsers>>,
          TError,
          Awaited<ReturnType<typeof getUsers>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsers<
  TData = Awaited<ReturnType<typeof getUsers>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  params?: GetUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUsers>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get the list of active users in the application.
 */

export function useGetUsers<
  TData = Awaited<ReturnType<typeof getUsers>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  params?: GetUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUsers>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetUsersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Get recording the specified user has viewed.
 * @summary Get recording the specified user has viewed.
 */
export const getUserViewedRecordings = (
  userId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<ViewedRecordingDetails[]>(
    { method: "GET", signal, url: `/api/v2/users/${userId}/viewed-recordings` },
    options,
  );
};

export const getGetUserViewedRecordingsQueryKey = (userId: number) => {
  return [`/api/v2/users/${userId}/viewed-recordings`] as const;
};

export const getGetUserViewedRecordingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserViewedRecordings>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserViewedRecordings>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUserViewedRecordingsQueryKey(userId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUserViewedRecordings>>
  > = ({ signal }) => getUserViewedRecordings(userId, requestOptions, signal);

  return {
    enabled: userId !== null && userId !== undefined,
    queryFn,
    queryKey,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserViewedRecordings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserViewedRecordingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserViewedRecordings>>
>;
export type GetUserViewedRecordingsQueryError = ErrorType<CommandErrorResponse>;

export function useGetUserViewedRecordings<
  TData = Awaited<ReturnType<typeof getUserViewedRecordings>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserViewedRecordings>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserViewedRecordings>>,
          TError,
          Awaited<ReturnType<typeof getUserViewedRecordings>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUserViewedRecordings<
  TData = Awaited<ReturnType<typeof getUserViewedRecordings>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserViewedRecordings>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserViewedRecordings>>,
          TError,
          Awaited<ReturnType<typeof getUserViewedRecordings>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUserViewedRecordings<
  TData = Awaited<ReturnType<typeof getUserViewedRecordings>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserViewedRecordings>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get recording the specified user has viewed.
 */

export function useGetUserViewedRecordings<
  TData = Awaited<ReturnType<typeof getUserViewedRecordings>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserViewedRecordings>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetUserViewedRecordingsQueryOptions(userId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Get several user-relevant statistics for the specified user.
 * @summary Get several user-relevant statistics for the specified user.
 */
export const getUserStatistics = (
  userId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<UserStatisticsDetails>(
    { method: "GET", signal, url: `/api/v2/users/${userId}/stats` },
    options,
  );
};

export const getGetUserStatisticsQueryKey = (userId: number) => {
  return [`/api/v2/users/${userId}/stats`] as const;
};

export const getGetUserStatisticsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserStatistics>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserStatistics>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUserStatisticsQueryKey(userId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUserStatistics>>
  > = ({ signal }) => getUserStatistics(userId, requestOptions, signal);

  return {
    enabled: userId !== null && userId !== undefined,
    queryFn,
    queryKey,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserStatistics>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserStatisticsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserStatistics>>
>;
export type GetUserStatisticsQueryError = ErrorType<CommandErrorResponse>;

export function useGetUserStatistics<
  TData = Awaited<ReturnType<typeof getUserStatistics>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserStatistics>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserStatistics>>,
          TError,
          Awaited<ReturnType<typeof getUserStatistics>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUserStatistics<
  TData = Awaited<ReturnType<typeof getUserStatistics>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserStatistics>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserStatistics>>,
          TError,
          Awaited<ReturnType<typeof getUserStatistics>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUserStatistics<
  TData = Awaited<ReturnType<typeof getUserStatistics>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserStatistics>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get several user-relevant statistics for the specified user.
 */

export function useGetUserStatistics<
  TData = Awaited<ReturnType<typeof getUserStatistics>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserStatistics>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetUserStatisticsQueryOptions(userId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Get events the specified user is participating in including status of attendance and event
 *  recording views for each event.
 * @summary Get events the specified user is participating in including status of attendance and event
 recording views for each event.
 */
export const getUserEventsParticipation = (
  userId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<UserEventParticipationDetails[]>(
    {
      method: "GET",
      signal,
      url: `/api/v2/users/${userId}/events-participation`,
    },
    options,
  );
};

export const getGetUserEventsParticipationQueryKey = (userId: number) => {
  return [`/api/v2/users/${userId}/events-participation`] as const;
};

export const getGetUserEventsParticipationQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserEventsParticipation>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserEventsParticipation>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUserEventsParticipationQueryKey(userId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUserEventsParticipation>>
  > = ({ signal }) =>
    getUserEventsParticipation(userId, requestOptions, signal);

  return {
    enabled: userId !== null && userId !== undefined,
    queryFn,
    queryKey,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserEventsParticipation>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserEventsParticipationQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserEventsParticipation>>
>;
export type GetUserEventsParticipationQueryError =
  ErrorType<CommandErrorResponse>;

export function useGetUserEventsParticipation<
  TData = Awaited<ReturnType<typeof getUserEventsParticipation>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserEventsParticipation>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserEventsParticipation>>,
          TError,
          Awaited<ReturnType<typeof getUserEventsParticipation>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUserEventsParticipation<
  TData = Awaited<ReturnType<typeof getUserEventsParticipation>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserEventsParticipation>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserEventsParticipation>>,
          TError,
          Awaited<ReturnType<typeof getUserEventsParticipation>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUserEventsParticipation<
  TData = Awaited<ReturnType<typeof getUserEventsParticipation>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserEventsParticipation>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get events the specified user is participating in including status of attendance and event
 recording views for each event.
 */

export function useGetUserEventsParticipation<
  TData = Awaited<ReturnType<typeof getUserEventsParticipation>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  userId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUserEventsParticipation>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetUserEventsParticipationQueryOptions(
    userId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Get a user's details.
 * @summary Get a user's details.
 */
export const getUser = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<UserPublicDetails>(
    { method: "GET", signal, url: `/api/v2/users/${id}` },
    options,
  );
};

export const getGetUserQueryKey = (id: number) => {
  return [`/api/v2/users/${id}`] as const;
};

export const getGetUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getUser>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUser>>> = ({
    signal,
  }) => getUser(id, requestOptions, signal);

  return {
    enabled: id !== null && id !== undefined,
    queryFn,
    queryKey,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetUserQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUser>>
>;
export type GetUserQueryError = ErrorType<CommandErrorResponse>;

export function useGetUser<
  TData = Awaited<ReturnType<typeof getUser>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  id: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUser>>,
          TError,
          Awaited<ReturnType<typeof getUser>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUser<
  TData = Awaited<ReturnType<typeof getUser>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUser>>,
          TError,
          Awaited<ReturnType<typeof getUser>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUser<
  TData = Awaited<ReturnType<typeof getUser>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get a user's details.
 */

export function useGetUser<
  TData = Awaited<ReturnType<typeof getUser>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUser>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetUserQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Get all the custom user fields defined.
 *
 *  <p>This is only used in <code>Reports.tsx</code> and only ever uses the title so I dumbed it down.
 * @deprecated
 * @summary Get all the custom user fields defined
 */
export const getUserFields = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<UserFieldDetails[]>(
    { method: "GET", signal, url: `/api/v2/users/fields` },
    options,
  );
};

export const getGetUserFieldsQueryKey = () => {
  return [`/api/v2/users/fields`] as const;
};

export const getGetUserFieldsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserFields>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getUserFields>>, TError, TData>
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserFieldsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserFields>>> = ({
    signal,
  }) => getUserFields(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserFields>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserFieldsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserFields>>
>;
export type GetUserFieldsQueryError = ErrorType<CommandErrorResponse>;

export function useGetUserFields<
  TData = Awaited<ReturnType<typeof getUserFields>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserFields>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserFields>>,
          TError,
          Awaited<ReturnType<typeof getUserFields>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUserFields<
  TData = Awaited<ReturnType<typeof getUserFields>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserFields>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserFields>>,
          TError,
          Awaited<ReturnType<typeof getUserFields>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUserFields<
  TData = Awaited<ReturnType<typeof getUserFields>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserFields>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @deprecated
 * @summary Get all the custom user fields defined
 */

export function useGetUserFields<
  TData = Awaited<ReturnType<typeof getUserFields>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserFields>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetUserFieldsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
